import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { moneySubUnitAmountAtLeast } from '../../../util/validators';

import { FieldCurrencyInput, Form, Button, SecondaryButton, InlineTextButton } from '../../../components';

import css from './MakeOfferSection.module.css';

/**
 * Nextmine: lets the customer propose a price on a "Buy now" listing
 * (default-purchase process, unitType 'item'). No payment happens here -
 * the seller must accept the offer first (see transactionProcessPurchase.js
 * make-offer / provider-accept-offer transitions).
 *
 * @component
 * @param {Object} props
 * @param {string} props.formId
 * @param {Object} props.currencyConfig
 * @param {Function} props.onSubmitOffer - receives { offerAmount: Money }
 * @param {boolean} props.sendOfferInProgress
 * @param {propTypes.error} props.sendOfferError
 * @param {boolean} props.isOwnListing
 * @returns {JSX.Element}
 */
const MakeOfferSection = props => {
  const intl = useIntl();
  const {
    formId,
    currencyConfig,
    onSubmitOffer,
    sendOfferInProgress,
    sendOfferError,
    isOwnListing,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  if (isOwnListing) {
    return null;
  }

  if (!isOpen) {
    return (
      <InlineTextButton
        type="button"
        className={css.openButton}
        onClick={() => setIsOpen(true)}
      >
        <FormattedMessage id="MakeOfferSection.openButton" />
      </InlineTextButton>
    );
  }

  return (
    <FinalForm
      onSubmit={values => {
        const { offerAmount } = values;
        onSubmitOffer({ offerAmount }).catch(() => {
          // Error is shown via sendOfferError, nothing more to do here.
        });
      }}
      render={fieldRenderProps => {
        const { handleSubmit, invalid, pristine, form } = fieldRenderProps;
        const submitInProgress = sendOfferInProgress;
        const submitDisabled = invalid || pristine || submitInProgress;
        const errorMessage = sendOfferError ? (
          <p className={css.error}>
            <FormattedMessage id="MakeOfferSection.submitFailed" />
          </p>
        ) : null;

        return (
          <Form id={formId} className={css.root} onSubmit={handleSubmit}>
            <FieldCurrencyInput
              id={`${formId}.offerAmount`}
              name="offerAmount"
              className={css.input}
              label={intl.formatMessage({ id: 'MakeOfferSection.offerLabel' })}
              placeholder={intl.formatMessage({ id: 'MakeOfferSection.offerPlaceholder' })}
              currencyConfig={currencyConfig}
              validate={moneySubUnitAmountAtLeast(
                intl.formatMessage({ id: 'MakeOfferSection.offerTooLow' }),
                1
              )}
            />
            {errorMessage}
            <div className={css.buttonRow}>
              <Button
                type="submit"
                className={css.submitButton}
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="MakeOfferSection.submitButton" />
              </Button>
              <SecondaryButton
                type="button"
                className={css.cancelButton}
                onClick={() => {
                  form.reset();
                  setIsOpen(false);
                }}
                disabled={submitInProgress}
              >
                <FormattedMessage id="MakeOfferSection.cancelButton" />
              </SecondaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default MakeOfferSection;
